import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaSave } from "react-icons/fa";
import {
  settingsService,
  giftVoucherService,
  Settings,
  GiftVoucher,
} from "../../services/firebase";
import "./GiftVoucher.css";

const statusLabel = (voucher: GiftVoucher): string => {
  if (voucher.status === "redeemed") return "מומש";
  if (voucher.status === "active" && voucher.paymentStatus === "paid") {
    return "פעיל";
  }
  return "ממתין לתשלום";
};

const formatDate = (value: any): string => {
  if (!value) return "-";
  try {
    const date =
      typeof value?.toDate === "function" ? value.toDate() : new Date(value);
    return date.toLocaleDateString("he-IL");
  } catch {
    return "-";
  }
};

const AdminGiftVoucher: React.FC = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<Settings | null>(null);
  const [price, setPrice] = useState<string>("");
  const [vouchers, setVouchers] = useState<GiftVoucher[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await settingsService.get();
        setSettings(data);
        if (typeof data.voucherPricePerPerson === "number") {
          setPrice(String(data.voucherPricePerPerson));
        }
      } catch (error) {
              }

      try {
        const allVouchers = await giftVoucherService.getAll();
        setVouchers(allVouchers);
      } catch (error) {
              }
    };

    fetchData();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    const numericPrice = Number(price);
    if (!price || Number.isNaN(numericPrice) || numericPrice <= 0) {
      setMessage({ type: "error", text: "אנא הזן מחיר תקין (גדול מ-0)." });
      setIsLoading(false);
      return;
    }

    const roundedPrice = Math.round(numericPrice * 100) / 100;

    try {
      await settingsService.update({
        ...(settings || {}),
        voucherPricePerPerson: roundedPrice,
      });
      setMessage({ type: "success", text: "מחיר השובר נשמר בהצלחה!" });
    } catch (error) {
            setMessage({ type: "error", text: "שגיאה בשמירה. נסה שוב." });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="admin-voucher-page">
      <div className="admin-voucher-header">
        <h1 className="admin-voucher-title">{t("admin.giftVoucher")}</h1>
        <p className="admin-voucher-description">
          קביעת מחיר שובר מתנה לאדם וניהול השוברים שנרכשו.
        </p>
      </div>

      {message && (
        <div className={`admin-voucher-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <form className="admin-voucher-form" onSubmit={handleSubmit}>
        <div className="admin-voucher-form-group">
          <label className="admin-voucher-label">מחיר שובר מתנה לאדם (₪)</label>
          <input
            className="admin-voucher-input"
            type="number"
            min="0.01"
            step="0.01"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="לדוגמה: 500 או 0.10"
          />
          <small className="admin-voucher-small-text">
            המחיר הכולל של שובר מחושב לפי מחיר זה כפול מספר המשתתפים שנבחר.
          </small>
        </div>

        <button
          className="admin-voucher-save-button"
          type="submit"
          disabled={isLoading}
        >
          <FaSave />
          {isLoading ? "שומר..." : "שמור מחיר"}
        </button>
      </form>

      <div className="admin-voucher-list-section">
        <h2 className="admin-voucher-section-title">שוברים שנרכשו</h2>
        {vouchers.length === 0 ? (
          <p className="admin-voucher-empty">עדיין לא נרכשו שוברים.</p>
        ) : (
          <div className="admin-voucher-table-wrapper">
            <table className="admin-voucher-table">
              <thead>
                <tr>
                  <th>קוד</th>
                  <th>משתתפים</th>
                  <th>סכום</th>
                  <th>רוכש</th>
                  <th>אימייל</th>
                  <th>סטטוס</th>
                  <th>תאריך</th>
                </tr>
              </thead>
              <tbody>
                {vouchers.map((voucher) => (
                  <tr key={voucher.id}>
                    <td className="admin-voucher-code-cell">
                      {voucher.code || "-"}
                    </td>
                    <td>{voucher.participants}</td>
                    <td>₪{voucher.amountPaid}</td>
                    <td>{voucher.purchaserName}</td>
                    <td>{voucher.purchaserEmail}</td>
                    <td>
                      <span
                        className={`admin-voucher-status admin-voucher-status-${voucher.status}`}
                      >
                        {statusLabel(voucher)}
                      </span>
                    </td>
                    <td>{formatDate(voucher.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminGiftVoucher;
